import { SortableHeader } from '@/components/search/SortableHeader'
import { groupLabel } from '@/components/usergroup/group'
import React, { useEffect, useMemo, useState } from 'react'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { BackButton } from '@/components/layout/NavBar'
import { useModal } from '@/components/modal/Modal'
import { StaticPaginatedTable } from '@/components/search/StaticPaginatedTable'
import { SearchInput } from '@/components/search/SearchInput'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { Roles } from '@/services/repository/users'

import { GeoAlertEditPopup, GeoAlertRemovePopup, GeoAlertRow, SimpleUserGroup } from './common'

const GeoAlertMenuPopup = ({
  onClose,
  row,
  groups,
  onReload,
}: {
  onClose: () => void
  row: GeoAlertRow
  groups: SimpleUserGroup[]
  onReload: () => void
}) => {
  const { openModal } = useModal()

  const menu: Menu = {
    title: row.name,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'elements.table.geo_alerts.menu.edit.edit_row',
        action: () => openModal((props) => <GeoAlertEditPopup {...props} geoAlertId={row.id} groups={groups} onSaved={onReload} />),
      },
      {
        icon: 'svgfont-remove',
        title: 'elements.table.geo_alerts.menu.edit.remove',
        action: () => openModal((props) => <GeoAlertRemovePopup {...props} geoAlert={row} onRemoved={onReload} />),
      },
    ],
  }

  return <MenuPopupBody menu={menu} onClose={onClose} />
}

export const CommunalGeoAlertsManagerPage = () => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const [groups, setGroups] = useState<SimpleUserGroup[] | null>(null)
  const [geoAlerts, setGeoAlerts] = useState<GeoAlertRow[] | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const groupsMap = useMemo(() => {
    return (groups || []).reduce((acc, group) => {
      acc[group.id] = groupLabel(group)
      return acc
    }, {} as Record<string, string>)
  }, [groups])

  const loadData = async () => {
    const groupsResponse = await services.session.fetchWithToken('/api/communal/usergroups/my')
    const nextGroups = groupsResponse.status === 200
      ? await groupsResponse.json().catch(() => [])
      : []

    const safeGroups = Array.isArray(nextGroups) ? nextGroups : []
    setGroups(safeGroups)

    if (safeGroups.length === 0) {
      setGeoAlerts([])
      return
    }

    const responses = await Promise.all(
      safeGroups.map((group) => services.session.fetchWithToken(`/api/communal/geo-alerts/byusersgroup/${group.id}`))
    )

    const data = await Promise.all(responses.map(async (response) => {
      if (response.status !== 200) {
        return []
      }
      const json = await response.json().catch(() => [])
      return Array.isArray(json) ? json : []
    }))

    const merged = data.flat().reduce((acc, row: GeoAlertRow) => {
      acc[row.id] = row
      return acc
    }, {} as Record<string, GeoAlertRow>)

    setGeoAlerts(Object.values(merged))
  }

  useEffect(() => {
    loadData()
  }, [])

  const filtered = useMemo(() => {
    if (!geoAlerts) {
      return null
    }
    if (!debouncedSearch) {
      return geoAlerts
    }

    const phrase = debouncedSearch.toLocaleLowerCase()
    return geoAlerts.filter((row) => {
      return [
        row.name,
        row.message,
        groupsMap[row.usersGroupId] || '',
        String(row.radius),
        row.alertOnEnter ? 'enter' : '',
        row.alertOnExit ? 'exit' : '',
      ].some((value) => value.toLocaleLowerCase().includes(phrase))
    })
  }, [geoAlerts, debouncedSearch, groupsMap])

  if (!session || !translates || !groups || !geoAlerts) {
    return <Spinner />
  }

  if (![Roles.SuperAdmin, Roles.HyperAdmin, Roles.CompanyAdmin, Roles.CompanyManager, Roles.Accountant].includes(session.user.role as Roles)) {
    return <LoggedInPageLayout
      pageName='geo-alerts-manager'
      services={services}
      session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}
    >
      <div className='critic-error-box'>{translates.for('errors.no_permissions')}</div>
    </LoggedInPageLayout>
  }

  return <LoggedInPageLayout
    pageName='geo-alerts-manager'
    services={services}
    session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}
  >
    <StaticPaginatedTable<GeoAlertRow>
      sortValues={{
        usersGroup: row => groupsMap[row.usersGroupId],
        machinesGroup: row => groupsMap[row.machinesGroupId],
        name: row => row.name,
        radius: row => row.radius,
        directions: row => [row.alertOnEnter ? translates.for('elements.table.geo_alerts.alert_on_enter_short') : '', row.alertOnExit ? translates.for('elements.table.geo_alerts.alert_on_exit_short') : ''].filter(Boolean).join(' / '),
        isActive: row => row.isActive
      }}
      data={filtered || []}
      translates={translates}
      toolboxLeft={() => <>
        {groups.length > 0 ? <div
          className='tool'
          onClick={() => openModal((props) => <GeoAlertEditPopup {...props} groups={groups} onSaved={loadData} />)}
        >
          <i className='svgfont-add' />
        </div> : null}
      </>}
      toolboxRight={() => <>
        <SearchInput translates={translates} value={search} onChange={setSearch} onDebouncedChange={setDebouncedSearch} />
      </>}
      header={(query, onSort) => <>
        <SortableHeader field='usersGroup' query={query} onSort={onSort}>{translates.for('elements.table.geo_alerts.users_group')}</SortableHeader>
        <SortableHeader field='machinesGroup' query={query} onSort={onSort}>{translates.for('elements.table.geo_alerts.machines_group')}</SortableHeader>
        <SortableHeader field='name' query={query} onSort={onSort}>{translates.for('elements.table.geo_alerts.name')}</SortableHeader>
        <SortableHeader field='radius' query={query} onSort={onSort}>{translates.for('elements.table.geo_alerts.radius')}</SortableHeader>
        <SortableHeader field='directions' query={query} onSort={onSort}>{translates.for('elements.table.geo_alerts.directions')}</SortableHeader>
        <SortableHeader field='isActive' query={query} onSort={onSort}>{translates.for('elements.table.geo_alerts.is_active')}</SortableHeader>
        <div className='hcol'></div>
      </>}
      row={(row) => <>
        <div className='col'>{groupsMap[row.usersGroupId] || '-'}</div>
        <div className='col'>{groupsMap[row.machinesGroupId] || '-'}</div>
        <div className='col'>{row.name}</div>
        <div className='col'>{row.radius} m</div>
        <div className='col'>
          {[
            row.alertOnEnter ? translates.for('elements.table.geo_alerts.alert_on_enter_short') : null,
            row.alertOnExit ? translates.for('elements.table.geo_alerts.alert_on_exit_short') : null,
          ].filter(Boolean).join(', ')}
        </div>
        <div className='col'>{row.isActive ? translates.for('bool.true') : translates.for('bool.false')}</div>
        <div className='col icocol' onClick={() => openModal((props) => <GeoAlertMenuPopup {...props} row={row} groups={groups} onReload={loadData} />)}>
          <i className='svgfont-menu' />
        </div>
      </>}
    />
    {groups.length === 0 ? <div className='critic-error-box'>{translates.for('elements.table.geo_alerts.empty_groups')}</div> : null}
  </LoggedInPageLayout>
}
